/**
 * DevOpsConsole — единая страница DevOps с вкладками.
 *
 * P0: Дашборд / Процессы / Сервисы / Контейнеры
 * P1: Файлы / Сеть / Nginx
 * P2: Cron / Backup / Security / Packages
 * P3 wave-1: Терминал / Audit / Настройки
 */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Activity,
  Cpu,
  Settings2,
  Boxes,
  FolderTree,
  Network,
  Globe,
  Clock,
  Database,
  ShieldCheck,
  Package,
  ShieldAlert,
  Sliders,
  Terminal as TerminalIcon,
} from "lucide-react";
import { MetricsDashboard } from "./MetricsDashboard";
import { ProcessTable } from "./ProcessTable";
import { ServiceTable } from "./ServiceTable";
import { ContainerTable } from "./ContainerTable";
import { HostFileBrowser } from "./HostFileBrowser";
import { NetworkPanel } from "./NetworkPanel";
import { NginxPanel } from "./NginxPanel";
import { CronPanel } from "./CronPanel";
import { BackupPanel } from "./BackupPanel";
import { SecurityPanel } from "./SecurityPanel";
import { PackagesPanel } from "./PackagesPanel";
import { AuditPanel } from "./AuditPanel";
import { DevopsSettingsPanel } from "./DevopsSettingsPanel";
import { LiveTerminalPanel } from "./LiveTerminalPanel";
import { TerminalDrawer } from "./TerminalDrawer";
import { cn } from "../../lib/cn";

export type DevOpsTab =
  | "dashboard"
  | "processes"
  | "services"
  | "containers"
  | "files"
  | "network"
  | "nginx"
  | "cron"
  | "backup"
  | "security"
  | "packages"
  | "terminal"
  | "audit"
  | "settings";

export interface DevOpsTabMeta {
  key: DevOpsTab;
  label: string;
  icon: typeof Activity;
  hint: string;
  /** true — отображается в верхней панели DevOpsConsole; false — в левом сайдбаре приложения. */
  topLevel: boolean;
}

/** Полный список вкладок. Порядок задаёт визуальный порядок в обоих местах. */
export const DEVOPS_TABS: DevOpsTabMeta[] = [
  // Top — оперативный мониторинг хоста, вызывается чаще всего.
  { key: "dashboard", label: "Дашборд", icon: Activity, hint: "Метрики VPS", topLevel: true },
  { key: "processes", label: "Процессы", icon: Cpu, hint: "Host PIDs", topLevel: true },
  { key: "services", label: "Сервисы", icon: Settings2, hint: "Systemd", topLevel: true },
  { key: "containers", label: "Контейнеры", icon: Boxes, hint: "Docker", topLevel: true },
  // Side — управленческие/реже используемые секции.
  { key: "files", label: "Файлы", icon: FolderTree, hint: "Host filesystem", topLevel: false },
  { key: "network", label: "Сеть", icon: Network, hint: "Порты, соединения, firewall", topLevel: false },
  { key: "nginx", label: "Nginx", icon: Globe, hint: "Сайты, логи, сертификаты", topLevel: false },
  { key: "cron", label: "Cron", icon: Clock, hint: "Crontabs + /etc/cron.*", topLevel: false },
  { key: "backup", label: "Backup", icon: Database, hint: "Volumes / pg_dump / архивы", topLevel: false },
  { key: "security", label: "Security", icon: ShieldCheck, hint: "Logins / SSH / fail2ban / sudo", topLevel: false },
  { key: "packages", label: "Packages", icon: Package, hint: "apt list/upgrade/install", topLevel: false },
  { key: "terminal", label: "Терминал", icon: TerminalIcon, hint: "Live-shell на хосте", topLevel: false },
  { key: "audit", label: "Audit", icon: ShieldAlert, hint: "Журнал DevOps-действий", topLevel: false },
  { key: "settings", label: "Настройки", icon: Sliders, hint: "Whitelist опасных команд", topLevel: false },
];

const TOP_TABS = DEVOPS_TABS.filter((t) => t.topLevel);

interface DevOpsConsoleProps {
  /** Внешний переключатель вкладки (например, из Cmd+K). */
  initialTab?: DevOpsTab;
  /** Версия — изменяется снаружи, чтобы заставить переключиться даже при том же initialTab. */
  navVersion?: number;
}

export function DevOpsConsole({ initialTab, navVersion }: DevOpsConsoleProps = {}) {
  const [tab, setTab] = useState<DevOpsTab>(initialTab || "dashboard");
  const [terminalOpen, setTerminalOpen] = useState(false);

  useEffect(() => {
    if (initialTab) setTab(initialTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab, navVersion]);

  return (
    <div className="h-full flex flex-col">
      <div className="border-b border-border/40 bg-bg-card/40 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex gap-1 overflow-x-auto items-center">
          <div className="flex gap-1 flex-1 overflow-x-auto">
            {TOP_TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={cn(
                    "shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-small transition-colors",
                    active
                      ? "bg-accent-500/10 text-accent-500 border border-accent-500/30"
                      : "text-fg-muted hover:text-fg hover:bg-bg-subtle border border-transparent",
                  )}
                  title={t.hint}
                >
                  <Icon className="size-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setTerminalOpen(true)}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-small bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors"
            title="Открыть live-терминал в боковой панели"
          >
            <TerminalIcon className="size-3.5" />
            Терминал
          </button>
        </div>
      </div>

      <motion.div
        key={tab}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="flex-1 overflow-auto"
      >
        {tab === "dashboard" && <MetricsDashboard />}
        {tab === "processes" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <ProcessTable />
          </div>
        )}
        {tab === "services" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <ServiceTable />
          </div>
        )}
        {tab === "containers" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <ContainerTable />
          </div>
        )}
        {tab === "files" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6 h-full min-h-[640px]">
            <HostFileBrowser />
          </div>
        )}
        {tab === "network" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <NetworkPanel />
          </div>
        )}
        {tab === "nginx" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <NginxPanel />
          </div>
        )}
        {tab === "cron" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <CronPanel />
          </div>
        )}
        {tab === "backup" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <BackupPanel />
          </div>
        )}
        {tab === "security" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <SecurityPanel />
          </div>
        )}
        {tab === "packages" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <PackagesPanel />
          </div>
        )}
        {tab === "terminal" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <LiveTerminalPanel />
          </div>
        )}
        {tab === "audit" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <AuditPanel />
          </div>
        )}
        {tab === "settings" && (
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <DevopsSettingsPanel />
          </div>
        )}
      </motion.div>

      <TerminalDrawer open={terminalOpen} onClose={() => setTerminalOpen(false)} />
    </div>
  );
}
